import { useState } from "react";
import { useApplicationForm } from "@/context/ApplicationFormContext";
import { EquipmentType, EQUIPMENT_LABELS, validateEDRPOU } from "@/lib/application";
import { cn } from "@/lib/utils";

const EQUIPMENT_LIST: EquipmentType[] = [
  "generator_petrol",
  "generator_diesel",
  "generator_gas",
  "inverter",
  "battery_controller",
  "batteries",
  "solar_panels",
];

interface FieldProps {
  label: string;
  required?: boolean;
  children: React.ReactNode;
  hint?: string;
  error?: string;
}

function Field({ label, required, children, hint, error }: FieldProps) {
  return (
    <div className="space-y-1.5">
      <label className="text-[13px] font-medium">
        {label}
        {required && <span className="text-destructive ml-0.5">*</span>}
      </label>
      {children}
      {error ? (
        <p className="text-[11px] text-destructive">{error}</p>
      ) : hint ? (
        <p className="text-[11px] text-muted-foreground">{hint}</p>
      ) : null}
    </div>
  );
}

function CheckIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
    </svg>
  );
}

function LightbulbIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 18h6" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M10 22h4" />
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M12 2a6 6 0 00-3.6 10.8c.8.6 1.6 1.6 1.6 2.7V16h4v-.5c0-1.1.8-2.1 1.6-2.7A6 6 0 0012 2z"
      />
    </svg>
  );
}

function EquipmentIcon({
  type,
  className,
}: {
  type: EquipmentType;
  className?: string;
}) {
  const baseProps = {
    className,
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: 1.8,
    strokeLinecap: "round" as const,
    strokeLinejoin: "round" as const,
  };

  switch (type) {
    case "generator_petrol":
      return (
        <svg {...baseProps}>
          <rect x="5" y="4" width="9" height="16" rx="2" />
          <path d="M14 7h2.5a2.5 2.5 0 012.5 2.5V18a2 2 0 01-2 2" />
          <path d="M8 8h4" />
          <path d="M7 12h5" />
        </svg>
      );
    case "generator_diesel":
      return (
        <svg {...baseProps}>
          <rect x="7" y="3" width="10" height="18" rx="2" />
          <path d="M7 7h10" />
          <path d="M7 13h10" />
          <path d="M7 19h10" />
        </svg>
      );
    case "generator_gas":
      return (
        <svg {...baseProps}>
          <path d="M12 2.5c3 2.6 5 5.8 5 8.8 0 3.7-2.8 6.4-5 7.2-2.2-.8-5-3.5-5-7.2 0-3 2-6.2 5-8.8z" />
          <path d="M12 9.5c1.4 1.5 2.2 3 2.2 4.4 0 1.8-1.3 3-2.2 3.4-.9-.4-2.2-1.6-2.2-3.4 0-1.4.8-2.9 2.2-4.4z" />
        </svg>
      );
    case "inverter":
      return (
        <svg {...baseProps}>
          <rect x="4" y="5" width="16" height="14" rx="3" />
          <path d="M13 7l-4 5h3l-1 5 4-6h-3z" />
        </svg>
      );
    case "battery_controller":
      return (
        <svg {...baseProps}>
          <rect x="4" y="6" width="16" height="12" rx="2" />
          <path d="M8 10h8" />
          <circle cx="10" cy="10" r="1.5" />
          <path d="M8 14h8" />
          <circle cx="14" cy="14" r="1.5" />
        </svg>
      );
    case "batteries":
      return (
        <svg {...baseProps}>
          <rect x="3.5" y="7" width="15" height="10" rx="2" />
          <path d="M18.5 10h2v4h-2" />
          <path d="M6.5 10h6" />
        </svg>
      );
    case "solar_panels":
      return (
        <svg {...baseProps}>
          <rect x="3" y="11" width="18" height="8" rx="1" />
          <path d="M3 15h18" />
          <path d="M9 11v8" />
          <path d="M15 11v8" />
          <circle cx="18" cy="6" r="2" />
          <path d="M18 2.5v1.5" />
          <path d="M18 8.5v1.5" />
          <path d="M15.5 6h-1.5" />
          <path d="M21.5 6h-1.5" />
        </svg>
      );
    default:
      return (
        <svg {...baseProps}>
          <rect x="4" y="6" width="16" height="12" rx="2" />
        </svg>
      );
  }
}

export function StepEquipment() {
  const { formData, updateEquipment } = useApplicationForm();
  const { equipment } = formData;
  const [supplierEdrpouError, setSupplierEdrpouError] = useState<string | null>(null);

  const handleSupplierEdrpouChange = (value: string) => {
    const formatted = value.replace(/\D/g, "").slice(0, 8);
    updateEquipment({ supplier_edrpou: formatted });
    
    // Validate when 8 digits entered
    if (formatted.length === 8) {
      const result = validateEDRPOU(formatted);
      setSupplierEdrpouError(result.valid ? null : result.error || null);
    } else {
      setSupplierEdrpouError(null);
    }
  };

  const handleSupplierEdrpouBlur = (value: string) => {
    if (value && value.length > 0) {
      const result = validateEDRPOU(value);
      setSupplierEdrpouError(result.valid ? null : result.error || null);
    }
  };

  const toggleEquipment = (type: EquipmentType) => {
    const current = equipment.equipment_types;
    const updated = current.includes(type)
      ? current.filter((t) => t !== type)
      : [...current, type];
    updateEquipment({ equipment_types: updated });
  };

  return (
    <div className="space-y-6">
      {/* Equipment selection */}
      <div>
        <h3 className="text-[15px] font-semibold mb-2">Обладнання для закупівлі</h3>
        <p className="text-[12px] text-muted-foreground mb-4">
          Оберіть типи обладнання, які плануєте придбати (можна обрати декілька)
        </p>

        <div className="grid sm:grid-cols-2 gap-3">
          {EQUIPMENT_LIST.map((type) => {
            const isSelected = equipment.equipment_types.includes(type);

            return (
              <button
                key={type}
                type="button"
                onClick={() => toggleEquipment(type)}
                className={cn(
                  "flex items-center gap-4 p-4 rounded-2xl border-[3px] text-left transition-all",
                  isSelected
                    ? "border-primary bg-primary/5"
                    : "border-border/50 hover:border-foreground/20"
                )}
              >
                <div
                  className={cn(
                    "w-12 h-12 rounded-xl flex items-center justify-center shrink-0 transition-colors",
                    isSelected ? "bg-primary/10 text-primary" : "bg-muted text-muted-foreground"
                  )}
                >
                  <EquipmentIcon type={type} className="w-6 h-6" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className={cn("text-[13px] font-medium", isSelected && "text-primary")}>
                    {EQUIPMENT_LABELS[type]}
                  </p>
                </div>
                <div
                  className={cn(
                    "w-6 h-6 rounded-lg border-2 flex items-center justify-center shrink-0 transition-all",
                    isSelected
                      ? "bg-primary border-primary"
                      : "border-muted-foreground/30"
                  )}
                >
                  {isSelected && <CheckIcon className="w-4 h-4 text-primary-foreground" />}
                </div>
              </button>
            );
          })}
        </div>

        {equipment.equipment_types.length > 0 && (
          <div className="mt-4 p-3 bg-muted/50 rounded-xl">
            <p className="text-[12px] text-muted-foreground">
              Обрано: <span className="font-medium text-foreground">{equipment.equipment_types.length}</span> типів обладнання
            </p>
          </div>
        )}
      </div>

      {/* Supply contract toggle */}
      <div className="bg-white rounded-2xl border-[3px] border-border/50 p-4">
        <label className="flex items-start gap-4 cursor-pointer">
          <div className="pt-0.5">
            <button
              type="button"
              onClick={() => updateEquipment({ has_supply_contract: !equipment.has_supply_contract })}
              className={cn(
                "w-12 h-7 rounded-full transition-all relative",
                equipment.has_supply_contract ? "bg-primary" : "bg-muted"
              )}
            >
              <div
                className={cn(
                  "absolute top-1 w-5 h-5 rounded-full bg-white shadow transition-all",
                  equipment.has_supply_contract ? "left-6" : "left-1"
                )}
              />
            </button>
          </div>
          <div className="flex-1">
            <p className="text-[14px] font-medium">Вже є укладений договір поставки</p>
            <p className="text-[12px] text-muted-foreground mt-0.5">
              Якщо ви вже уклали договір на поставку обладнання, вкажіть його реквізити
              (надає пріоритет при розгляді заявки)
            </p>
          </div>
        </label>
      </div>

      {/* Contract details */}
      {equipment.has_supply_contract && (
        <div className="space-y-4 bg-blue-50/50 rounded-2xl border-[3px] border-blue-100 p-5">
          <h3 className="text-[15px] font-semibold text-blue-900">Реквізити договору поставки</h3>

          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Номер договору" required>
              <input
                type="text"
                value={equipment.contract_number}
                onChange={(e) => updateEquipment({ contract_number: e.target.value })}
                placeholder="№123/2026"
                className="w-full h-11 px-4 rounded-xl border-2 border-blue-200 bg-white text-[13px] placeholder:text-muted-foreground focus:outline-none focus:border-blue-400 transition-colors"
              />
            </Field>

            <Field label="Дата договору" required>
              <input
                type="date"
                value={equipment.contract_date}
                onChange={(e) => updateEquipment({ contract_date: e.target.value })}
                max={new Date().toISOString().split("T")[0]}
                className="w-full h-11 px-4 rounded-xl border-2 border-blue-200 bg-white text-[13px] placeholder:text-muted-foreground focus:outline-none focus:border-blue-400 transition-colors"
              />
            </Field>
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Назва постачальника" required>
              <input
                type="text"
                value={equipment.supplier_name}
                onChange={(e) => updateEquipment({ supplier_name: e.target.value })}
                placeholder="ТОВ «Енергообладнання»"
                className="w-full h-11 px-4 rounded-xl border-2 border-blue-200 bg-white text-[13px] placeholder:text-muted-foreground focus:outline-none focus:border-blue-400 transition-colors"
              />
            </Field>

            <Field 
              label="ЄДРПОУ постачальника" 
              required 
              hint={!supplierEdrpouError ? "8 цифр" : undefined}
              error={supplierEdrpouError || undefined}
            >
              <input
                type="text"
                value={equipment.supplier_edrpou}
                onChange={(e) => handleSupplierEdrpouChange(e.target.value)}
                onBlur={(e) => handleSupplierEdrpouBlur(e.target.value)}
                placeholder="12345678"
                className={cn(
                  "w-full h-11 px-4 rounded-xl border-2 bg-white text-[13px] font-mono placeholder:text-muted-foreground focus:outline-none transition-colors",
                  supplierEdrpouError
                    ? "border-destructive/50 focus:border-destructive"
                    : "border-blue-200 focus:border-blue-400"
                )}
              />
            </Field>
          </div>

          <Field label="Опис обладнання за договором" required>
            <textarea
              value={equipment.equipment_description}
              onChange={(e) => updateEquipment({ equipment_description: e.target.value })}
              placeholder="Назва, модель, комплектація обладнання"
              rows={3}
              className="w-full px-4 py-3 rounded-xl border-2 border-blue-200 bg-white text-[13px] placeholder:text-muted-foreground focus:outline-none focus:border-blue-400 transition-colors resize-none"
            />
          </Field>

          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Вартість обладнання (грн)" required hint="Повна вартість з ПДВ">
              <input
                type="number"
                value={equipment.equipment_cost || ""}
                onChange={(e) => {
                  const val = parseFloat(e.target.value);
                  updateEquipment({ equipment_cost: isNaN(val) ? null : val });
                }}
                placeholder="150000"
                min={0}
                className="w-full h-11 px-4 rounded-xl border-2 border-blue-200 bg-white text-[13px] placeholder:text-muted-foreground focus:outline-none focus:border-blue-400 transition-colors"
              />
            </Field>

            <Field label="Строк поставки" required hint="Не пізніше 20.12.2026">
              <input
                type="date"
                value={equipment.delivery_deadline}
                onChange={(e) => updateEquipment({ delivery_deadline: e.target.value })}
                max="2026-12-20"
                className="w-full h-11 px-4 rounded-xl border-2 border-blue-200 bg-white text-[13px] placeholder:text-muted-foreground focus:outline-none focus:border-blue-400 transition-colors"
              />
            </Field>
          </div>

          <div className="bg-blue-100 rounded-xl p-3 mt-2">
            <div className="flex items-start gap-2 text-blue-700">
              <LightbulbIcon className="w-4 h-4 mt-0.5 shrink-0" />
              <p className="text-[12px]">
                Пріоритет мають заявки з договором, який передбачає поставку обладнання протягом 45 календарних днів
                з дня подачі заяви (але не пізніше 20.12.2026). Такі заяви розглядаються першочергово.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
